import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import type { BranchApprovalStatus, OwnerTenantBranch } from '@his/shared';
import { del, get, post, put } from '@/lib/api';
import { dateOnly } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { OwnerConfirmDialog } from './OwnerConfirm';

/**
 * A hospital's sites, from the owner console (SRS 5.1).
 *
 * Opening a second site is the most common thing a growing hospital rings
 * support about, and it used to require a Super Admin at the hospital's end.
 *
 * Closing one is deliberately not a delete. Every patient, invoice and admission
 * recorded at a site points at its branch; the row has to survive for the
 * history to mean anything. The server refuses to close a branch that still has
 * patients admitted, or the last open one — a hospital with no branch is a
 * hospital nobody can register a patient at.
 */

interface BranchForm {
  code: string;
  name: string;
  city: string;
  phone: string;
}

const APPROVAL_LABEL: Record<BranchApprovalStatus, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

const APPROVAL_VARIANT: Record<BranchApprovalStatus, 'warning' | 'success' | 'destructive'> = { pending: 'warning', approved: 'success', rejected: 'destructive' };

const EMPTY: BranchForm = { code: '', name: '', city: '', phone: '' };

export function TenantBranchesPanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ branch: OwnerTenantBranch | null } | null>(null);
  const [form, setForm] = useState<BranchForm>(EMPTY);
  const [closing, setClosing] = useState<OwnerTenantBranch | null>(null);
  const [reopening, setReopening] = useState<OwnerTenantBranch | null>(null);

  const branches = useQuery({
    queryKey: ['tenant-branches', tenantId],
    queryFn: () => get<OwnerTenantBranch[]>(`/platform/tenants/${tenantId}/branches`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-branches', tenantId] });
    // The tenant header carries a branch count that is now stale.
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
  };

  const open = (branch: OwnerTenantBranch | null) => {
    setForm(
      branch
        ? { code: branch.code, name: branch.name, city: branch.city ?? '', phone: branch.phone ?? '' }
        : EMPTY,
    );
    setEditing({ branch });
  };

  const formValid = /^[A-Z0-9]{2,10}$/.test(form.code.trim().toUpperCase()) && form.name.trim().length >= 2;

  return (
    <>
      <Alert className="mb-4">
        A branch is a physical site. Staff are assigned to one, and a user whose every role names a
        branch sees only that site's patients, stock and takings — so adding one here changes who can
        see what, and asks for your 2FA code like every other owner action.
      </Alert>

      <div className="mb-3 flex justify-end">
        <Button onClick={() => open(null)}>
          <Building2 className="me-1.5 h-4 w-4" /> Open a branch
        </Button>
      </div>

      <DataTable
        rows={branches.data}
        loading={branches.isLoading} refreshing={branches.isFetching}
        error={branches.error}
        rowKey={(row) => row.id}
        empty="This facility has no branches yet."
        columns={[
          {
            header: 'Branch',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <div className="flex items-center gap-1.5 font-medium">
                  {row.name}
                  {!row.isActive && <Badge variant="secondary">Closed</Badge>}
                  <Badge variant={APPROVAL_VARIANT[row.approvalStatus]}>{APPROVAL_LABEL[row.approvalStatus]}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.code}
                  {row.city ? ` · ${row.city}` : ''}
                </div>
                {row.approvalStatus === 'rejected' && row.rejectionReason && (
                  <div className="mt-0.5 text-xs text-destructive">Reason: {row.rejectionReason}</div>
                )}
              </div>
            ),
          },
          { header: 'Phone', sort: (row) => row.phone, cell: (row) => row.phone ?? '—' },
          { header: 'Staff', align: 'right', sort: (row) => row.userCount, cell: (row) => row.userCount },
          { header: 'Departments', align: 'right', sort: (row) => row.departmentCount, cell: (row) => row.departmentCount },
          { header: 'Opened', sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => open(row)}>
                  Edit
                </Button>
                {row.isActive ? (
                  <Button variant="destructive" size="sm" onClick={() => setClosing(row)}>
                    Close
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => setReopening(row)}>
                    Reopen
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {editing && (
        <OwnerConfirmDialog
          open
          onClose={() => setEditing(null)}
          title={editing.branch ? `Edit ${editing.branch.name}` : 'Open a branch'}
          description={
            editing.branch
              ? 'The branch code appears on every document raised at this site.'
              : 'The new site starts empty — departments, wards and stores are set up by the hospital.'
          }
          confirmLabel={editing.branch ? 'Save branch' : 'Open branch'}
          extraValid={formValid}
          successMessage={editing.branch ? 'Branch updated' : 'Branch opened'}
          action={({ totpCode, reason }) => {
            const payload = {
              totpCode,
              reason,
              code: form.code.trim().toUpperCase(),
              name: form.name.trim(),
              city: form.city.trim() || undefined,
              phone: form.phone.trim() || undefined,
            };
            return editing.branch
              ? put(`/platform/tenants/${tenantId}/branches/${editing.branch.id}`, payload)
              : post(`/platform/tenants/${tenantId}/branches`, payload);
          }}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code" required hint="Uppercase letters and digits, 2–10 characters">
              <Input
                value={form.code}
                maxLength={10}
                className="tabular uppercase"
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <Field label="Phone">
              <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
          </div>
        </OwnerConfirmDialog>
      )}

      {closing && (
        <OwnerConfirmDialog
          open
          onClose={() => setClosing(null)}
          title={`Close ${closing.name}`}
          destructive
          confirmLabel="Close branch"
          warning={
            <>
              Nothing is deleted. The site stops accepting new work and disappears from the branch
              switcher; every record raised there stays readable and reportable. Refused while
              patients are still admitted there.
            </>
          }
          successMessage="Branch closed"
          action={(payload) => del(`/platform/tenants/${tenantId}/branches/${closing.id}`, { body: payload })}
          onDone={() => {
            setClosing(null);
            refresh();
          }}
        />
      )}

      {reopening && (
        <OwnerConfirmDialog
          open
          onClose={() => setReopening(null)}
          title={`Reopen ${reopening.name}`}
          confirmLabel="Reopen branch"
          successMessage="Branch reopened"
          action={(payload) =>
            post(`/platform/tenants/${tenantId}/branches/${reopening.id}/reopen`, payload)
          }
          onDone={() => {
            setReopening(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
